import type {
  TcRegion,
  TcTcpsApiService,
  ViewDetailsResponse,
} from '@data-access/connect/tc-tcps';
import type {
  DataLoadingService,
  ProjectDataState,
} from '@mobile-mapping/providers';
import type { ViewMode } from '@mobile-mapping/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import type { SetStoreFunction } from 'solid-js/store';
import type { ILogger } from '@trimble/logging';
import { asError } from '@trimble/logging';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import {
  MeasurementsCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  ViewsCapabilityProvider,
  ViewWithClipping,
  convertScan3dStyleToPointCloudStyling,
} from '@trimble/web3d-providers';
import { ClippingCapability } from '@trimble/web3d-capabilities';
import { PaneSyncer } from '@trimble/pane-sync';
import type { CapabilityRegistry } from '@trimble/web3d';
import type { Scan3dStyle } from '@trimble/types';
import type { MultipaneReservedData } from './multipane-view.types';
import { applyMultipaneLayout, applyPaneState } from './apply-multipane-layout';
import {
  applyNavigationStates,
  applyPerPaneViewData,
  applyTrajectories,
} from './apply-multipane-view-data';
import { parseMultipaneReservedData } from './parse-multipane-reserved-data';
import { fromReservedMeasurements } from './reserved-measurements';
import { restoreMeasurementLabels } from './measurement-labels';

export type LoadViewWithReservedDataDeps = {
  readonly tcTcpsApi: Pick<TcTcpsApiService, 'getReservedViewData'>;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly dataLoadingService: DataLoadingService;
  readonly dataState: ProjectDataState;
  readonly layouts: PaneLayouts<ViewMode>;
  readonly setLayouts: SetStoreFunction<PaneLayouts<ViewMode>>;
  readonly region: TcRegion;
  readonly logger: ILogger;
};

/**
 * Loads a saved view with numark reserved data: layout → pane state → shared
 * data → trajectories → navigation states → per-pane data.
 */
export async function loadViewWithReservedData(
  view: ViewDetailsResponse,
  deps: LoadViewWithReservedDataDeps
): Promise<void> {
  const {
    tcTcpsApi,
    capabilityRegistry,
    dataLoadingService,
    dataState,
    layouts,
    setLayouts,
    region,
    logger,
  } = deps;

  const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider)();
  if (!viewsProvider) return;

  let reserved: MultipaneReservedData | null = null;
  try {
    const dto = await tcTcpsApi.getReservedViewData(region, view.id, 'numark');
    const parsed = parseMultipaneReservedData(dto.data);
    reserved = parsed.data;
    if (parsed.outdated) {
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.views.outdatedReservedData'),
      });
    }
  } catch (error: unknown) {
    logger.debug(
      'Reserved view data unavailable, using single pane',
      undefined,
      asError(error)
    );
  }

  // Connect 3D (TRCPS) stores scan ids and scan3dStyle on reserved app=trcps,
  // not on scan-data view_{id}. Numark views keep using scan-data via loadView.
  let trcpsScans: unknown = null;
  if (!reserved) {
    try {
      const trcpsDto = await tcTcpsApi.getReservedViewData(
        region,
        view.id,
        'trcps'
      );
      trcpsScans =
        trcpsDto.data && typeof trcpsDto.data === 'object'
          ? trcpsDto.data['scans']
          : null;
    } catch (error: unknown) {
      logger.debug(
        'TRCPS reserved view data unavailable',
        undefined,
        asError(error)
      );
    }
  }
  const trcpsScanIds = extractScanIdsFromTrcpsReserved(trcpsScans);

  try {
    if (reserved) PaneSyncer.setIsLoadingView(true);
    applyMultipaneLayout(layouts, setLayouts, reserved);
    await applyPaneState(capabilityRegistry, reserved);
    await viewsProvider.loadView(view, region, {
      skipCamera: !!reserved,
    });

    if (!reserved && trcpsScanIds.length > 0) {
      const scan3dStyle = extractScan3dStyleFromTrcpsReserved(trcpsScans);
      const stylingProvider = capabilityRegistry.get(
        PointCloudStylingCapabilityProvider
      )();
      if (scan3dStyle && stylingProvider) {
        stylingProvider.clearUserClassOverrides();
        stylingProvider.setPointCloudStyling(
          convertScan3dStyleToPointCloudStyling(scan3dStyle)
        );
      }
      dataLoadingService.loadExclusive('pointCloud', trcpsScanIds);
    }

    // Alignment (and other non-Connect-native) measurements live in
    // reserved data, so restore them after loadView re-seeds the native
    // markups. A failure here must not abort the rest of the view load.
    if (reserved?.measurements?.length) {
      try {
        const measurementsProvider = capabilityRegistry.get(
          MeasurementsCapabilityProvider
        )();
        measurementsProvider?.restoreMeasurements(
          fromReservedMeasurements(reserved.measurements)
        );
      } catch (error: unknown) {
        logger.error(
          `Failed to restore measurements for view ${view.id}`,
          undefined,
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'warning',
          message: t('mobileMapping.views.measurementsRestoreFailed'),
        });
      }
    }

    // Runs even without reserved data so legacy/Connect-only views still get
    // names; a failure must not abort the rest of the load.
    try {
      const measurementsProvider = capabilityRegistry.get(
        MeasurementsCapabilityProvider
      )();
      if (measurementsProvider) {
        restoreMeasurementLabels(
          measurementsProvider,
          reserved?.connectMeasurementData ?? []
        );
      }
    } catch (error: unknown) {
      logger.error(
        `Failed to restore measurement labels for view ${view.id}`,
        undefined,
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.views.measurementNamesRestoreFailed'),
      });
    }

    // Reserved data applies clippings pane by pane; otherwise restore here.
    if (!reserved) {
      const viewWithClipping = view as ViewWithClipping;
      for (const pane of capabilityRegistry.paneContainers()) {
        const clippingCap = pane.get(ClippingCapability)();
        if (clippingCap) {
          clippingCap.loadClippingFromConnectData(
            view.sectionPlanes,
            viewWithClipping.sectionBox
          );
        }
      }
    }

    await applyTrajectories(dataLoadingService, dataState, reserved);
    await applyNavigationStates(capabilityRegistry, reserved);
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        resolve(applyPerPaneViewData(capabilityRegistry, reserved));
      });
    });
  } finally {
    PaneSyncer.setIsLoadingView(false);
  }
}

const SCAN3D_STYLE_KEYS = [
  'renderingMode',
  'blendIntensity',
  'showEyeDomeLighting',
  'pointBudget',
  'pointDensity',
  'pointSize',
  'classificationSchemes',
  'intensityOffset',
  'intensityStartColor',
  'intensityEndColor',
  'clearanceDefaultTransparent',
  'clearanceDefaultColor',
  'clearanceBands',
  'elevationReference',
  'elevationTolerance',
  'elevationBandCount',
  'elevationOffset',
] as const;

function extractScanIdsFromTrcpsReserved(scans: unknown): string[] {
  if (!Array.isArray(scans)) {
    return [];
  }

  const ids: string[] = [];
  for (const scan of scans) {
    if (typeof scan === 'string' && scan !== '') {
      ids.push(scan);
      continue;
    }
    if (hasNonEmptyStringId(scan)) {
      ids.push(scan.id);
    }
  }
  return ids;
}

function extractScan3dStyleFromTrcpsReserved(
  scans: unknown
): Scan3dStyle | undefined {
  if (!Array.isArray(scans)) {
    return undefined;
  }

  for (const scan of scans) {
    if (!hasScan3dStyleProperty(scan)) {
      continue;
    }
    if (isScan3dStyle(scan.scan3dStyle)) {
      return scan.scan3dStyle;
    }
  }

  return undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasNonEmptyStringId(value: unknown): value is { id: string } {
  return (
    isPlainObject(value) &&
    typeof value['id'] === 'string' &&
    value['id'] !== ''
  );
}

function hasScan3dStyleProperty(
  value: unknown
): value is { scan3dStyle: unknown } {
  return isPlainObject(value) && 'scan3dStyle' in value;
}

function isScan3dStyle(value: unknown): value is Scan3dStyle {
  if (!isPlainObject(value)) {
    return false;
  }
  return SCAN3D_STYLE_KEYS.some((key) => key in value);
}
